import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlaidLink, PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import SDAGoBack from "./SDAGoBack";
import SDASteps from "./SDASteps";
import { SDATestModeBanner, SDATestSkipButton } from "./sdaTestMode";
import {
  getMyIdentityVerification,
  createIdentityLinkToken,
  postIdentityVerificationCallback,
  IdentityVerificationStatus as IdvStatus,
} from "../../api/services";
import { useAuth } from "../../contexts/AuthContext";

const SDAIdentity: React.FC = () => {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [status, setStatus] = useState<IdvStatus | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * Flips true when Plaid returns a non-terminal status after the user has
   * completed the modal (pending_review on their end). Different from the
   * initial `in-progress` state where we still need to open the modal.
   */
  const [awaitingReview, setAwaitingReview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await getMyIdentityVerification();
        if (cancelled) return;
        setStatus(res.data?.status ?? null);

        if (res.data?.status !== "completed") {
          const tokenRes = await createIdentityLinkToken();
          if (!cancelled) setLinkToken(tokenRes.data.link_token);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(t("sda.identity.errorLoading"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const onPlaidSuccess = async (
    _publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    const linkSessionId = metadata.link_session_id;
    if (!linkSessionId) {
      setError(t("sda.identity.errorNoSession"));
      return;
    }
    try {
      // Server hits Plaid to confirm; we use the authoritative status it
      // returns rather than assuming success.
      const res = await postIdentityVerificationCallback(linkSessionId);
      setStatus(res.data.status);
      setError(null);
      setAwaitingReview(false);
      if (res.data.status === "failed") {
        setError(t("sda.identity.errorFailed"));
      } else if (res.data.status === "in-progress") {
        // Plaid sent us back into `in-progress` after the modal closed —
        // that's their pending_review state. Block progression and show
        // an explanatory message.
        setAwaitingReview(true);
      }
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("sda.identity.errorCallback"),
      );
    }
  };

  const handleRetry = async () => {
    setError(null);
    setLoading(true);
    try {
      const tokenRes = await createIdentityLinkToken();
      setLinkToken(tokenRes.data.link_token);
      setStatus("in-progress");
    } catch (err) {
      console.error(err);
      setError(t("sda.identity.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const isVerified = status === "completed";
  const isFailed = status === "failed";
  // Ops can flip this on the user row to let specific tenants skip IDV.
  // The step UI stays visible — they can still verify if they want —
  // but the Next button isn't gated on it.
  const canProceedWithoutVerification =
    user?.skipIdentityVerificationRequirement === true;

  return (
    <div>
      <SDAGoBack />

      <SDATestModeBanner />

      <SDASteps current={2} />

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-primary">
            {t("sda.identity.title")}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {isVerified
              ? t("sda.identity.subAlreadyVerified")
              : t("sda.identity.subDefault")}
          </p>
        </div>

        <div className="p-8 text-center">
          {loading ? (
            <p className="text-sm text-muted">{t("dashboard.loading")}</p>
          ) : isVerified ? (
            <>
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
                <svg
                  className="h-7 w-7 text-success"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h4 className="mb-2 text-lg font-semibold text-primary">
                {t("sda.identity.verifiedHeading")}
              </h4>
              <p className="mx-auto max-w-md text-sm text-gray-600">
                {t("sda.identity.verifiedBody")}
              </p>
            </>
          ) : awaitingReview ? (
            <>
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
                <svg
                  className="h-7 w-7 text-warning"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h4 className="mb-2 text-lg font-semibold text-primary">
                {t("sda.identity.reviewHeading")}
              </h4>
              <p className="mx-auto max-w-md text-sm text-gray-600">
                {t("sda.identity.reviewBody")}
              </p>
            </>
          ) : isFailed ? (
            <>
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <svg
                  className="h-7 w-7 text-danger"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h4 className="mb-2 text-lg font-semibold text-primary">
                {t("sda.identity.failedHeading")}
              </h4>
              <p className="mx-auto mb-6 max-w-md text-sm text-gray-600">
                {t("sda.identity.failedBody")}
              </p>
              <button
                type="button"
                onClick={handleRetry}
                className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
              >
                {t("sda.identity.tryAgain")}
              </button>
            </>
          ) : (
            <>
              <img
                alt="Plaid"
                src="/assets/plaid.svg"
                className="mx-auto mb-6 h-7"
              />
              <h4 className="mb-2 text-lg font-semibold text-primary">
                {t("sda.identity.introHeading")}
              </h4>
              <p className="mx-auto mb-2 max-w-md text-sm text-gray-600">
                {t("sda.identity.introBody1")}
              </p>
              <p className="mx-auto mb-6 max-w-md text-sm text-muted">
                {t("sda.identity.introBody2")}
              </p>

              {error && (
                <div className="mx-auto mb-4 max-w-md rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
                  {error}
                </div>
              )}

              {linkToken ? (
                <PlaidLink
                  token={linkToken}
                  product={["identity_verification"]}
                  clientName="Lighthouse"
                  onSuccess={onPlaidSuccess}
                  onExit={(err, metadata) =>
                    console.error("Plaid Identity exit", err, metadata)
                  }
                  style={{
                    backgroundColor: "#ffc107",
                    color: "#000",
                    padding: "0.625rem 1.5rem",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    border: 0,
                    cursor: "pointer",
                  }}
                  className="transition-colors hover:!bg-accent-hover"
                >
                  {t("sda.identity.startButton")}
                </PlaidLink>
              ) : (
                <div className="mx-auto max-w-md rounded-lg bg-amber-50 px-4 py-3 text-sm text-warning">
                  {t("sda.identity.noLinkToken")}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <SDATestSkipButton
            onSkip={() => navigate(`/sda/${uuid}/financial`)}
          />
          <button
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            disabled={!isVerified && !canProceedWithoutVerification}
            onClick={() => navigate(`/sda/${uuid}/financial`)}
          >
            {t("sda.identity.next")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SDAIdentity;
